import type { AiMode, LastMessagePreview, MessageAttachment } from '@/hooks/use-conversations'
import { previewText } from './message-content'
import { API_BASE } from '@/lib/config'
import { platformLabelOf } from '@/lib/platform-labels'
import { i18n } from '@/lib/i18n'

// ── Proxy ảnh CDN Zalo qua backend ──────────────────────────────────
// Ảnh khách gửi lưu dưới host `*.zdn.vn`/`*.zadn.vn`; ở production CSP `img-src`
// của origin phục vụ FE chặn các host này nên ảnh vỡ. Nạp qua proxy cùng origin
// API (`/media/zalo-image`) thì luôn hiển thị được, không phụ thuộc CSP ngoài.
const ZALO_CDN_HOST = /(^|\.)(zdn|zadn)\.vn$|(^|\.)zaloapp\.com$/i

export function proxyZaloMedia(url?: string | null): string | undefined {
  if (!url) return undefined
  try {
    const u = new URL(url)
    if (u.protocol === 'https:' && ZALO_CDN_HOST.test(u.hostname)) {
      return `${API_BASE}/media/zalo-image?u=${encodeURIComponent(url)}`
    }
  } catch {
    /* URL không hợp lệ → trả nguyên trạng */
  }
  return url
}

// ── Nhãn kênh theo mã platform ──────────────────────────────────────
// Dùng chung bảng `lib/platform-labels` (khớp backend) — trước đây chép tay, thiếu Zalo Bot.
export function platformLabel(platform?: number | null): string {
  return platformLabelOf(platform, i18n.t('convList:lib.otherChannel'))
}

// ── Nhãn chế độ AI ──────────────────────────────────────────────────
export const AI_MODES: { value: AiMode; label: string; hint: string }[] = [
  { value: 'manual', get label() { return i18n.t('convList:lib.ai.manual') }, get hint() { return i18n.t('convList:lib.ai.manualHint') } },
  { value: 'suggest', get label() { return i18n.t('convList:lib.ai.suggest') }, get hint() { return i18n.t('convList:lib.ai.suggestHint') } },
  { value: 'auto', get label() { return i18n.t('convList:lib.ai.auto') }, get hint() { return i18n.t('convList:lib.ai.autoHint') } },
]

export function aiModeLabel(mode: AiMode): string {
  return AI_MODES.find((m) => m.value === mode)?.label ?? mode
}

// ── Định dạng thời gian ─────────────────────────────────────────────
export function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const diff = Date.now() - d.getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return i18n.t('common:time.justNow')
  if (min < 60) return i18n.t('common:time.minutesAgo', { count: min })
  const hours = Math.floor(min / 60)
  if (hours < 24) return i18n.t('common:time.hoursAgo', { count: hours })
  const days = Math.floor(hours / 24)
  if (days < 7) return i18n.t('common:time.daysAgo', { count: days })
  return d.toLocaleDateString(i18n.language || 'vi-VN', { day: '2-digit', month: '2-digit' })
}

export function formatClock(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(i18n.language || 'vi-VN', { hour: '2-digit', minute: '2-digit' })
}

// ── Xem trước tin cuối trong danh sách ──────────────────────────────
export function messagePreview(msg?: LastMessagePreview | null): string {
  if (!msg) return i18n.t('convList:lib.noMessages')
  if (msg.isDeleted) return i18n.t('convList:lib.recalled')
  const prefix = msg.senderType === 'self' ? i18n.t('convList:lib.youPrefix') : ''
  // Dùng chung bộ diễn giải với bong bóng chat — trước đây nhánh `default` in
  // thẳng `content`, nên tin dạng JSON (sự kiện nhóm, cuộc gọi…) lòi mã ra danh sách.
  return prefix + previewText(msg)
}

// ── Tách ảnh/tệp từ content JSON (một số tin lưu content dạng JSON) ──
export interface ParsedMedia {
  imageUrl?: string
  fileHref?: string
  fileTitle?: string
  text?: string
}

export function parseContent(content: string | null | undefined, contentType: string): ParsedMedia {
  if (!content) return {}
  const trimmed = content.trim()
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const obj = JSON.parse(trimmed) as Record<string, unknown>
      if (contentType === 'image' || obj.thumb || (obj.href && String(obj.href).match(/\.(jpg|jpeg|png|gif|webp)/i))) {
        // Zalo gửi "ảnh kèm chữ" thì phần chữ nằm ở `title` — không lấy ra là
        // mất luôn lời nhắn của khách, chỉ còn mỗi ảnh.
        const caption = typeof obj.title === 'string' && obj.title.trim() ? obj.title.trim() : undefined
        return { imageUrl: (obj.hdUrl || obj.href || obj.thumb) as string | undefined, text: caption }
      }
      if (contentType === 'file' || obj.title) {
        return { fileHref: obj.href as string | undefined, fileTitle: (obj.title as string) || i18n.t('convList:lib.attachedFile') }
      }
    } catch {
      /* không phải JSON hợp lệ → coi như text */
    }
  }
  return { text: content }
}

// ── Lấy danh sách ảnh từ attachments ────────────────────────────────
export function imageAttachments(attachments?: MessageAttachment[]): string[] {
  if (!attachments?.length) return []
  return attachments
    .filter((a) => a.type === 'photo' || a.type === 'image')
    .map((a) => a.url)
}

export function fileAttachments(attachments?: MessageAttachment[]): MessageAttachment[] {
  if (!attachments?.length) return []
  return attachments.filter((a) => a.type === 'file' || a.type === 'share.file')
}

// ── Sticker Zalo ────────────────────────────────────────────────────
/**
 * Zalo không trả URL ảnh cho sticker — chỉ trả id. Ảnh lấy từ CDN theo id
 * (đúng pattern bản gốc dùng): .../emoticon/sticker/webpc?eid=<id>&size=<size>
 */
export function stickerUrl(id: number | string, size = 130): string {
  return `https://zalo-api.zadn.vn/api/emoticon/sticker/webpc?eid=${id}&size=${size}`
}

/**
 * Lấy URL ảnh sticker từ nội dung tin nhắn. Backend lưu nhiều dạng:
 *  - URL đầy đủ            → dùng luôn
 *  - JSON {"id":18009,...} → dựng URL từ id
 *  - chuỗi số "47240"      → dựng URL từ số đó
 */
/**
 * Link sticker nằm trong phần đính kèm — sticker khách gửi qua Zalo OA: `{ type: 'sticker', payload: { id, url } }`
 * (bản ghi cũ lưu nội dung rỗng; mã sticker OA là chuỗi hex nên không dựng link theo mã được).
 */
export function stickerUrlFromAttachments(attachments: unknown): string | null {
  if (!Array.isArray(attachments)) return null
  for (const a of attachments) {
    if (!a || typeof a !== 'object') continue
    const o = a as { url?: unknown; payload?: { url?: unknown; id?: unknown } }
    const url = typeof o.payload?.url === 'string' ? o.payload.url : typeof o.url === 'string' ? o.url : null
    if (url && /^https?:\/\//.test(url)) return url
    const id = o.payload?.id
    if (typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))) return stickerUrl(id)
  }
  return null
}

export function stickerUrlFromContent(content: string | null): string | null {
  if (!content) return null
  const raw = content.trim()
  if (/^https?:\/\//.test(raw)) return raw
  if (/^\d+$/.test(raw)) return stickerUrl(raw)
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>
    const id = obj.id ?? obj.stickerId ?? obj.sticker_id
    if (typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))) {
      return stickerUrl(id)
    }
  } catch {
    /* không phải JSON → bỏ qua */
  }
  return null
}
